<div class="company-table">
  <ng-template #emptyTpl>
    <div class="company-table__empty">
      <p class="company-table__empty-title">{{ emptyTitle() }}</p>
      <p class="company-table__empty-desc">{{ emptyDescription() }}</p>
    </div>
  </ng-template>

  <nz-table
    #dataTable
    class="company-table__grid"
    [nzData]="viewRows()"
    [nzLoading]="loading()"
    [nzBordered]="isBordered()"
    [nzSize]="tableSize()"
    [nzTableLayout]="tableLayout()"
    [nzFrontPagination]="nzFrontPagination()"
    [nzShowPagination]="nzShowPagination()"
    [nzShowSizeChanger]="showPageSizeChanger()"
    [nzPageSize]="pageSize()"
    [nzPageSizeOptions]="$any(pageSizeOptions())"
    [nzTotal]="pagination() === 'server' ? total() : viewRows().length"
    [nzPageIndex]="pagination() === 'server' ? pageIndex() : 1"
    [nzScroll]="nzScroll()"
    [nzNoResult]="emptyTpl"
    [nzPaginationType]="paginationType()"
    [nzPaginationPosition]="mergedConfig().paginationPosition"
    [nzSimple]="mergedConfig().simplePagination"
    (nzCurrentPageDataChange)="onCurrentPageDataChange($event)"
    (nzPageIndexChange)="onPageIndexChange($event)"
    (nzPageSizeChange)="onPageSizeChange($event)"
    (nzQueryParams)="onTableQueryParamsChange($event)"
  >
    <thead>
      <tr>
        @if (showCheckbox()) {
          <th
            nzWidth="48px"
            [(nzChecked)]="allChecked"
            [nzIndeterminate]="indeterminate"
            (nzCheckedChange)="onAllChecked($event)"
          ></th>
        }
        @for (col of displayColumns(); track col.field) {
          <th
            class="company-table__header-cell"
            [class.company-table__header-cell--resizable]="isColumnResizable(col)"
            [nzColumnKey]="col.sortable || col.nzFilters.length > 0 || col.isSearchFilter ? col.field : undefined"
            [nzWidth]="columnWidth(col)"
            [nzLeft]="col.pinned === 'left'"
            [nzRight]="col.pinned === 'right'"
            [nzShowSort]="col.sortable === true && !isResizingColumn()"
            [nzSortFn]="col.sortable ? (col.nzSortFn || false) : false"
            [nzSortDirections]="col.sortable ? ['ascend', 'descend', null] : []"
            [nzFilters]="col.nzFilters"
            [nzFilterFn]="col.nzFilterFn ?? false"
            [nzFilterMultiple]="filterMultipleEnabled(col)"
            [nzShowFilter]="col.nzFilters.length > 0"
            [nzCustomFilter]="col.isSearchFilter"
            [nzEllipsis]="isEllipsis() && !isColumnResizable(col)"
            (nzFilterChange)="onListFilterChange(col.field, $event)"
          >
            {{ col.headerName ?? col.field }}
            @if (col.isSearchFilter && searchDropdownMenu(); as searchMenu) {
              <nz-filter-trigger
                [nzVisible]="isSearchPanelVisible(col.field)"
                (nzVisibleChange)="onSearchPanelVisibleChange(col.field, $event)"
                [nzActive]="isSearchFilterActive(col.field)"
                [nzDropdownMenu]="searchMenu"
              >
                <span nz-icon nzType="search"></span>
              </nz-filter-trigger>
            }
            @if (col.headerTags?.length) {
              @for (tag of col.headerTags; track tag.label) {
                <nz-tag [nzColor]="tag.nzColor ?? undefined">{{ tag.label }}</nz-tag>
              }
            }
            @if (isColumnResizable(col)) {
              <div
                class="company-table__col-resize-handle"
                [class.company-table__col-resize-handle--active]="resizingColumnField() === col.field"
                role="separator"
                aria-orientation="vertical"
                [attr.aria-label]="'Redimensionar coluna ' + (col.headerName ?? col.field)"
                (pointerdown)="onColumnResizePointerDown($event, col)"
                (click)="preventHeaderSort($event)"
              ></div>
            }
          </th>
        }
        @if (hasRowActions()) {
          <th nzRight [nzWidth]="actionsColumnWidth() + 'px'">{{ actionsColumnLabel() }}</th>
        }
      </tr>
    </thead>
    <tbody>
      @for (row of dataTable.data; track row.id) {
        <tr
          [class.company-table__row--clickable]="isRowClickSelectionEnabled()"
          (click)="onRowClick(row, $event)"
        >
          @if (showCheckbox()) {
            <td
              [(nzChecked)]="row.checked"
              [nzDisabled]="row.disabled"
              (nzCheckedChange)="onRowCheckedChange()"
            ></td>
          }
          @for (col of displayColumns(); track col.field) {
            <td
              [class.company-table__cell--custom]="!!cellTemplateFor(col.field)"
              [nzLeft]="col.pinned === 'left'"
              [nzRight]="col.pinned === 'right'"
              [nzEllipsis]="isEllipsis()"
            >
              @if (cellTemplateFor(col.field); as cellTpl) {
                <ng-container
                  [ngTemplateOutlet]="cellTpl"
                  [ngTemplateOutletContext]="cellContext(row, col)"
                />
              } @else if (col.type === 'status') {
                <nz-tag [nzColor]="statusTagColor(row.record[col.field], col)" class="company-table__status-tag">
                  @if (statusShowsSpinner(row.record[col.field], col)) {
                    <span nz-icon nzType="loading" [nzSpin]="true" class="company-table__status-icon"></span>
                  }
                  {{ statusLabel(row.record[col.field], col) }}
                </nz-tag>
              } @else {
                @let cellText = formatCellValue(row.record[col.field], col);
                @if (isEllipsis()) {
                  <span
                    class="company-table__ellipsis-text"
                    nz-tooltip
                    [nzTooltipTitle]="ellipsisTooltip()"
                    (mouseenter)="onEllipsisTextEnter($event, cellText)"
                  >
                    {{ cellText }}
                  </span>
                } @else {
                  {{ cellText }}
                }
              }
            </td>
          }
          @if (hasRowActions()) {
            <td nzRight class="company-table__actions-cell">
              @for (action of rowActions(); track action.id) {
                @if (action.submenu?.length) {
                  <button
                    type="button"
                    nz-button
                    nzType="text"
                    nz-dropdown
                    nzTrigger="click"
                    nzPlacement="bottomRight"
                    class="company-table__action-btn"
                    nz-tooltip
                    [nzTooltipTitle]="action.label"
                    [attr.aria-label]="action.label"
                    [disabled]="isActionDisabled(action, row)"
                    [nzDropdownMenu]="submenuTpl"
                  >
                    <span nz-icon [nzType]="action.icon" [nzTheme]="action.nzTheme ?? 'outline'"></span>
                  </button>
                  <nz-dropdown-menu #submenuTpl="nzDropdownMenu">
                    <ul nz-menu>
                      @for (item of action.submenu; track item.id) {
                        <li nz-menu-item (click)="onSubmenuItemClick(item, row, $event)">
                          @if (item.icon) {
                            <span nz-icon [nzType]="item.icon" nzTheme="outline"></span>
                          }
                          <span class="company-table__submenu-label">{{ item.label }}</span>
                          @if (item.description) {
                            <span class="company-table__submenu-desc">{{ item.description }}</span>
                          }
                        </li>
                      }
                    </ul>
                  </nz-dropdown-menu>
                } @else {
                  <button
                    type="button"
                    nz-button
                    nzType="text"
                    class="company-table__action-btn"
                    [nzDanger]="action.nzDanger ?? false"
                    nz-tooltip
                    [nzTooltipTitle]="action.label"
                    [attr.aria-label]="action.label"
                    [disabled]="isActionDisabled(action, row)"
                    (click)="onRowActionClick(action, row, $event)"
                  >
                    <span nz-icon [nzType]="action.icon" [nzTheme]="action.nzTheme ?? 'outline'"></span>
                  </button>
                }
              }
            </td>
          }
        </tr>
      }
    </tbody>
  </nz-table>

  @if (hasSearchFilterColumns()) {
    <nz-dropdown-menu #searchDropdownMenu="nzDropdownMenu">
      @if (activeSearchField(); as searchField) {
        <div class="company-table__search-filter">
          <input
            nz-input
            type="text"
            [placeholder]="activeSearchPlaceholder()"
            [ngModel]="searchDraft(searchField)"
            (ngModelChange)="onSearchDraftChange(searchField, $event)"
            (keyup.enter)="applyActiveSearchFilter()"
          />
          <div class="company-table__search-filter-actions">
            <button
              nz-button
              nzSize="small"
              nzType="primary"
              type="button"
              (click)="applyActiveSearchFilter()"
            >
              Buscar
            </button>
            <button nz-button nzSize="small" type="button" (click)="resetActiveSearchFilter()">
              Limpar
            </button>
          </div>
        </div>
      }
    </nz-dropdown-menu>
  }
</div>
